import { useEffect, useMemo, useState } from "react";

// Case-insensitive substring match on the row's title and on each half of its subtitle. The subtitle
// arrives as its two plain strings rather than the composed sentence the row renders, because that
// sentence is a React node (the company half is a link) and searching one would throw.
function rowMatches(row, needle) {
  if (!needle) return true;
  const q = needle.toLowerCase();
  return [row.title, row.subtitleTitle, row.subtitleCompanyName].some((field) =>
    (field || "").toLowerCase().includes(q),
  );
}

/**
 * useRepresentsSection — presentation state for the tabbed <RepresentsSection> (the chrome's other half).
 *
 * Pure client-side view state over the tabs the engine already produced: which tab is active, the
 * search text, and the current page. It owns no data and performs no fetches — filtering and paging
 * happen in-memory over `activeTab.rows`. Switching tabs resets both the search and the page; typing
 * a new search resets the page (so a filtered list never strands the viewer on an empty page).
 *
 * @param {Object} options
 * @param {Array} options.tabs - TabDescriptors `{ key, label, rows, ... }` (opaque here)
 * @param {number} [options.perPage] - Rows per page (default 10); a tab's own `perPage` wins
 * @param {string} [options.defaultTabKey] - Initially active tab key (default: the first tab that has
 *   rows, re-derived as the engine's collections land, until the viewer selects one)
 * @param {string|null} [options.activeTabRequest] - A tab key to switch to once (e.g. after a
 *   successful add from a different tab); consumed and cleared via `onActiveTabRequestHandled`
 * @param {Function} [options.onActiveTabRequestHandled] - Called once `activeTabRequest` has been
 *   applied (or found not to match any tab), so the caller can clear it
 * @returns {{ activeTabKey: string, setActiveTabKey: Function, query: string, setQuery: Function,
 *   page: number, setPage: Function, activeTab: Object, visibleRows: Array, pageCount: number,
 *   filteredCount: number, perPage: number }}
 */
function useRepresentsSection({
  tabs,
  perPage = 10,
  defaultTabKey,
  activeTabRequest = null,
  onActiveTabRequestHandled = undefined,
}) {
  const [selectedTabKey, setSelectedTabKey] = useState(defaultTabKey || tabs[0]?.key);
  // The engine's collections arrive after mount, so the first render's tabs are all empty and any
  // choice derived from them is meaningless. Without this flag the hook would fight the viewer: a
  // silent reload that empties the tab they are reading would yank them somewhere else mid-read.
  const [userChoseTab, setUserChoseTab] = useState(false);
  const [query, setQueryRaw] = useState("");
  const [page, setPage] = useState(1);

  // An engine may ask the section to jump to a tab once — e.g. after a successful add lands the
  // new row on a tab the viewer isn't currently looking at. Applying (if the key matches a tab) and
  // clearing (either way) happen on the same pass, so this is consume-and-clear rather than
  // something needing a nonce: a remount with an already-consumed request has nothing to replay,
  // and a second add of the same type is a fresh null -> key transition. Keyed narrowly on
  // `activeTabRequest` itself — it must fire exactly once per request, not on every re-render that
  // happens to carry the same still-pending request.
  useEffect(() => {
    if (activeTabRequest == null) return;

    if (tabs.some((tab) => tab.key === activeTabRequest)) {
      setUserChoseTab(true);
      setSelectedTabKey(activeTabRequest);
      setQueryRaw("");
      setPage(1);
    }
    onActiveTabRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabRequest]);

  // Until the viewer picks a tab, keep re-deriving to the first one that actually has rows. A caller's
  // defaultTabKey is a stated intent rather than a guess, so it wins outright. Falling back to tabs[0]
  // (not "no tab") keeps an all-empty section on its first tab.
  const activeTabKey = useMemo(() => {
    if (defaultTabKey || userChoseTab) return selectedTabKey;
    return (tabs.find((tab) => (tab.rows || []).length > 0) || tabs[0])?.key;
  }, [defaultTabKey, userChoseTab, selectedTabKey, tabs]);

  const activeTab = useMemo(() => tabs.find((tab) => tab.key === activeTabKey) || tabs[0], [tabs, activeTabKey]);

  // Switching tabs starts fresh — the previous tab's search text and page have no meaning here.
  const setActiveTabKey = (key) => {
    setUserChoseTab(true);
    setSelectedTabKey(key);
    setQueryRaw("");
    setPage(1);
  };

  // A narrowed search may leave fewer pages than the one we're on, so jump back to the first.
  const setQuery = (value) => {
    setQueryRaw(value);
    setPage(1);
  };

  const filteredRows = useMemo(
    () => (activeTab?.rows || []).filter((row) => rowMatches(row, query)),
    [activeTab, query],
  );

  const filteredCount = filteredRows.length;
  const tabPerPage = activeTab?.perPage ?? perPage;
  const pageCount = Math.max(1, Math.ceil(filteredCount / tabPerPage));
  // Clamp so a stale page (e.g. after rows shrink) can't point past the end.
  const safePage = Math.min(page, pageCount);
  const visibleRows = useMemo(
    () => filteredRows.slice((safePage - 1) * tabPerPage, safePage * tabPerPage),
    [filteredRows, safePage, tabPerPage],
  );

  return {
    activeTabKey,
    setActiveTabKey,
    query,
    setQuery,
    page: safePage,
    setPage,
    activeTab,
    visibleRows,
    pageCount,
    filteredCount,
    perPage: tabPerPage,
  };
}

export default useRepresentsSection;
